import React, {
  ForwardedRef,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from "react";
import Box from "@mui/material/Box";
import Accordion, { AccordionProps } from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/material/styles";
import { useDrag, useDrop } from "react-dnd";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { SvgIcon } from "@mui/material";

import { CircularProgress } from "../../../../components";

import { useProductAnalyzer } from "../../../../contexts/productAnalyzerContext";
import { Panel } from "../../../../constants";
import { useAppSelector } from "../../../../store/hooks";
import { selectExtension } from "../../../../features/extension/extension.selector";

export type TCardPosition = {
  x: number;
  y: number;
};

interface CardProps {
  id: Panel | "EMPTY_PLACEHOLDER";
  x: number;
  y: number;
  children?: React.ReactNode;
  title?: string;
  Icon?: string;
  isCollapsible?: boolean;
  badge?: React.ReactNode;
  onDrop?: (from: TCardPosition, to: TCardPosition) => void;
}

const DND_TYPE = "CARD"; // Define ItemType for react-dnd

const StyledAccordion = forwardRef<HTMLElement, CardProps>(
  (
    { id, x, y, children, title, Icon, isCollapsible = true, badge, onDrop },
    jumpRef,
  ) => {
    const { panelStatus } = useProductAnalyzer();
    const isPlaceholder = (id as any) === "EMPTY_PLACEHOLDER";
    const status = isPlaceholder ? "succeeded" : panelStatus[id as Panel];
    const isLoaded = status === "succeeded";
    const ref = useRef<HTMLDivElement>(null);
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const { isExtension } = useAppSelector(selectExtension);

    useEffect(() => {
      if (isPlaceholder) return;
      const storageKey = `pa_panel_open_${id}`;
      const saved = localStorage.getItem(storageKey);
      if (saved !== null) {
        setIsExpanded(saved === "true");
        return;
      }
      setIsExpanded(isLoaded);
    }, [id, isLoaded, isPlaceholder]);

    const [, drop] = useDrop({
      accept: DND_TYPE,
      hover(item: { id: string; x: number; y: number }, monitor) {
        if (!ref.current) return;
        if (item.x === x && item.y === y) return;
        if (isPlaceholder && x === item.x && y - item.y === 1)
          return;
        // Only trigger hover when directly over this card (avoid nested)
        if (!monitor.isOver({ shallow: true })) return;

        const hoverBoundingRect = ref.current.getBoundingClientRect();
        const hoverMiddleY =
          (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

        const clientOffset = monitor.getClientOffset();
        if (!clientOffset) return;

        const hoverClientY = clientOffset.y - hoverBoundingRect.top;

        // Only move when cursor crosses half of the hovered item's height
        if (
          id !== "EMPTY_PLACEHOLDER" &&
          item.y < y &&
          hoverClientY < hoverMiddleY
        )
          return;
        if (
          id !== "EMPTY_PLACEHOLDER" &&
          item.y > y &&
          hoverClientY > hoverMiddleY
        )
          return;

        onDrop?.({ x: item.x, y: item.y }, { x, y });
        item.x = x;
        item.y = y;
      },
    });

    const [{ isDragging }, drag] = useDrag({
      type: DND_TYPE,
      item: { id, x, y },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    useEffect(() => {
      if (isPlaceholder) {
        drop(ref);
      } else {
        drag(drop(ref));
      }
    }, [id, ref, drag, drop, isPlaceholder]);

    if (isPlaceholder) {
      return (
        <Box
          ref={ref}
          sx={{ width: "100%", height: y === 0 ? "100vh" : "10px" }}
        ></Box>
      );
    }

    const handleExpandClick = () => {
      if (!isCollapsible) return;
      if (status === "loading" || status === "failed") return;
      setIsExpanded((prev: boolean) => {
        const next = !prev;
        if (!isPlaceholder) {
          const storageKey = `pa_panel_open_${id}`;
          localStorage.setItem(storageKey, String(next));
        }
        return next;
      });
    };

    return (
      <Box
        ref={jumpRef}
        sx={{
          opacity: isDragging ? 0.5 : 1,
          transition: "transform 200ms ease, opacity 200ms ease",
          margin: "0px 0px 5px 0px",
          scrollMarginTop: isExtension ? "36px" : "75px",
        }}
      >
        <Accordion
          expanded={isCollapsible ? isExpanded : true}
          sx={{
            boxShadow: "0px 2px 8px 0px rgba(16, 24, 40, 0.05)",
            "& .MuiCollapsible-root": (isCollapsible ? isExpanded : true)
              ? {
                  height: "auto !important",
                }
              : {},
          }}
        >
          <CustomAccordionSummary
            ref={ref}
            sx={{ cursor: "grab" }}
            onClick={handleExpandClick}
            expandIcon={
              isCollapsible
                ? status === "failed"
                  ? (
                <Box
                  width={"24px"}
                  height={"24px"}
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  <SvgIcon
                    component={ErrorOutlineIcon}
                    color="error"
                    sx={{ fontSize: "18px" }}
                  />
                </Box>
                    )
                  : status === "loading"
                    ? (
                <Box
                  width={"24px"}
                  height={"24px"}
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  <CircularProgress size="sm" color="inherit" />
                </Box>
                      )
                    : (
                <ExpandMoreIcon />
              )
                : undefined
            }
          >
            <Box
              display={"flex"}
              alignItems={"center"}
              gap={0.5}
              width={"100%"}
              position={"relative"}
            >
              {Icon && <img src={Icon} alt="icon" width={14} height={14} />}
              <Typography
                component="span"
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  textTransform: "capitalize",
                }}
              >
                {title}
              </Typography>
              {!!badge && (
                <Box position={"absolute"} right={"-4px"}>
                  {badge}
                </Box>
              )}
            </Box>
          </CustomAccordionSummary>
          <CustomAccordionDetail>{children}</CustomAccordionDetail>
        </Accordion>
      </Box>
    );
  },
);

const CustomAccordionSummary = styled(AccordionSummary)<AccordionProps>(() => ({
  padding: "4px 8px",
  background: "#F0F0F0",
  border: "1px solid #D4D6D8",
  minHeight: "30px !important",
  height: "30px",
  borderRadius: "4px 4px 0px 0px",
  margin: 0,
  display: "flex",
  alignItems: "center",
  "& .MuiAccordionSummary-content": {
    margin: "3px 0px",
  },
}));

const CustomAccordionDetail = styled(AccordionDetails)(() => ({
  border: "1px solid #D4D6D8",
  borderTop: "none",
  padding: 0,
  borderRadius: "0px 0px 4px 4px",
  position: "relative",
}));

export default StyledAccordion;
